import React, { useRef } from "react";
import { Form, Formik } from "formik";
import { useMutation } from "@apollo/client/react";
import * as Yup from "yup";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import Input from "src/components/form/Input";
import UnsavedChangesDialog from "src/components/modals/UnsavedChanges/UnsavedChangesDialog";
import useUnsavedChanges from "src/hooks/useUnsavedChanges";
import useNotification from "src/hooks/notification";
import { CREATE_COST_CENTER_MUTATION } from "src/constants/graphql/mutations";
import { CLIENTS_AND_PROJECTS_QUERY, FETCH_PROJECT_QUERY } from "src/constants/graphql/queries";

const validationSchema = Yup.object({
  costCenterName: Yup.string().required("Cost center name is required"),
  costCenterCode: Yup.string(),
  comments: Yup.string(),
});

export default function ReceivingCostCenterModal({ open, onClose, onSaved, projectId }) {
  const formikRef = useRef(null);
  const { notifyError } = useNotification();

  const {
    attemptClose,
    confirmDiscard,
    cancelDiscard,
    confirmSave,
    showDialog,
    saving: unsavedSaving,
  } = useUnsavedChanges({
    onClose,
    isDirtyFn: () => !!formikRef.current?.dirty,
    onSave: async () => {
      await formikRef.current?.submitForm();
    },
  });

  const [createCostCenter] = useMutation(CREATE_COST_CENTER_MUTATION, {
    refetchQueries: [
      { query: CLIENTS_AND_PROJECTS_QUERY },
      { query: FETCH_PROJECT_QUERY, variables: { id: projectId } },
    ],
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!projectId) {
      notifyError("Missing project for cost center creation");
      setSubmitting(false);
      return;
    }

    try {
      const { data } = await createCostCenter({
        variables: {
          projectId,
          costCenterName: values.costCenterName,
          costCenterCode: values.costCenterCode,
          comments: values.comments,
        },
      });

      const created = data?.createCostCenter?.costCenter;
      if (!created) {
        notifyError("Failed to create cost center");
        return;
      }

      onSaved?.(created);
      onClose();
    } catch (error) {
      notifyError(error.message || "Failed to create cost center");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={attemptClose} fullWidth maxWidth="xs">
        <DialogTitle sx={{ position: "relative", pb: 0, textAlign: "center" }}>
          <Typography fontSize={24} fontWeight={500} color="text.secondary">
            Add Cost Center
          </Typography>
          <IconButton
            aria-label="close"
            onClick={attemptClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Formik
          innerRef={formikRef}
          initialValues={{
            costCenterName: "",
            costCenterCode: "",
            comments: "",
          }}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {({ isSubmitting }) => (
            <Form id="receiving-cost-center-form">
              <DialogContent>
                <Box pt={2} pb={1}>
                  <Stack spacing={2}>
                    <Input fullWidth label="Cost Center Name" name="costCenterName" showNAButton={false} />
                    <Input fullWidth label="Order Number" name="costCenterCode" showNAButton={false} />
                    <Input fullWidth label="Comments" name="comments" showNAButton={false} />
                  </Stack>
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2 }}>
                <Box display="flex" gap={1} width="100%" justifyContent="flex-end">
                  <Button variant="outlined" onClick={attemptClose}>
                    Cancel
                  </Button>
                  <Button
                    form="receiving-cost-center-form"
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={18} /> : null}
                  >
                    Save
                  </Button>
                </Box>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
      {showDialog && (
        <UnsavedChangesDialog
          open={showDialog}
          onCancel={cancelDiscard}
          onDiscard={confirmDiscard}
          onSave={confirmSave}
          saving={unsavedSaving}
        />
      )}
    </>
  );
}
